import {
    Button,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { useFetch, useSnackbar } from "../../hooks";

import { BACKEND_ROUTES } from "../../backendRoutes";
import { DeleteUserRoleDialog } from "./DeleteUserRoleDialog";
import { MODEL_PROPTYPES } from "../../models";
import PropTypes from "prop-types";
import { UserRoleRow } from "./UserRoleRow";
import { roleWithPermissions } from "./utils";
import { useState } from "react";

const COLUMNS = ["User", "Role", "Permissions", "Auto-grant"];

export function UserRoleTable({
    userRoles,
    count,
    page,
    pageCount,
    onPageChange,
    roles,
    onUpdated,
    onDeleted,
}) {
    const { patch, destroy } = useFetch();
    const { openSnackbar } = useSnackbar();

    // { [userRoleUuid]: { roleUuid?, autoGrantAccess? } }, sent as PATCH bodies
    const [pendingChanges, setPendingChanges] = useState({});
    // Kept after closing so the dialog text doesn't change mid-transition
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const hasPendingChanges = Object.keys(pendingChanges).length > 0;

    const handleChange = (userRole, field, value) => {
        setPendingChanges((prev) => {
            const { [userRole.uuid]: rowChanges, ...rest } = prev;
            const next = { ...rowChanges, [field]: value };
            if (value === userRole[field]) delete next[field];
            return Object.keys(next).length
                ? { ...rest, [userRole.uuid]: next }
                : rest;
        });
    };

    const handleSave = async () => {
        setIsSubmitting(true);
        const entries = Object.entries(pendingChanges);
        const results = await Promise.all(
            entries.map(([uuid, body]) =>
                patch(`${BACKEND_ROUTES.USER_ROLE}/${uuid}`, { body })
            )
        );
        // Failed calls resolve to undefined; keep only those pending
        const failed = entries.filter((_, i) => results[i] === undefined);
        // Refetch first so saved rows don't flash their old values
        await onUpdated();
        setPendingChanges(Object.fromEntries(failed));
        setIsSubmitting(false);
        if (!failed.length) openSnackbar("Changes saved.", "success");
    };

    const handleOpenDelete = (userRole) => {
        setDeleteTarget(userRole);
        setIsDeleteOpen(true);
    };

    const handleConfirmDelete = async () => {
        const { uuid } = deleteTarget;
        setIsDeleteOpen(false);
        setIsSubmitting(true);
        // Without it the backend refuses to delete a role that has child roles
        const result = await destroy(
            `${BACKEND_ROUTES.USER_ROLE}/${uuid}?cascadeDelete=true`
        );
        setIsSubmitting(false);
        if (result !== undefined) {
            openSnackbar("User removed.", "success");
            setPendingChanges(({ [uuid]: _, ...rest }) => rest);
            onDeleted();
        }
    };

    return (
        <>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {COLUMNS.map((column) => (
                            <TableCell
                                key={column}
                                align={
                                    column === "Auto-grant" ? "center" : "left"
                                }
                            >
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    {column}
                                </Typography>
                            </TableCell>
                        ))}
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {userRoles.map((userRole) => (
                        <UserRoleRow
                            key={userRole.uuid}
                            userRole={userRole}
                            roles={roles}
                            changes={pendingChanges[userRole.uuid] ?? {}}
                            onChange={(field, value) =>
                                handleChange(userRole, field, value)
                            }
                            onDelete={() => handleOpenDelete(userRole)}
                            disabled={isSubmitting}
                        />
                    ))}
                </TableBody>
            </Table>
            <Divider sx={{ my: 2 }} />
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="caption" color="text.secondary">
                        {count} user(s)
                    </Typography>
                    {pageCount > 1 && (
                        // Pending changes are only visible on their own page
                        <Tooltip
                            title={
                                hasPendingChanges
                                    ? "Save or cancel your changes first"
                                    : ""
                            }
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={page === 0 || hasPendingChanges}
                                    onClick={() => onPageChange(page - 1)}
                                >
                                    Previous
                                </Button>
                                <Typography variant="caption">
                                    {page + 1} / {pageCount}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={
                                        page >= pageCount - 1 ||
                                        hasPendingChanges
                                    }
                                    onClick={() => onPageChange(page + 1)}
                                >
                                    Next
                                </Button>
                            </Stack>
                        </Tooltip>
                    )}
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setPendingChanges({})}
                        disabled={!hasPendingChanges || isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSave}
                        disabled={!hasPendingChanges || isSubmitting}
                    >
                        Save changes
                    </Button>
                </Stack>
            </Stack>
            <DeleteUserRoleDialog
                open={isDeleteOpen}
                userRole={deleteTarget}
                onClose={() => setIsDeleteOpen(false)}
                onConfirm={handleConfirmDelete}
            />
        </>
    );
}

UserRoleTable.propTypes = {
    userRoles: PropTypes.arrayOf(PropTypes.shape(MODEL_PROPTYPES.UserRole))
        .isRequired,
    count: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    pageCount: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    roles: PropTypes.arrayOf(roleWithPermissions).isRequired,
    onUpdated: PropTypes.func.isRequired,
    onDeleted: PropTypes.func.isRequired,
};
